/**
 * /journey — Member Fitness Journey screen.
 * Reads fitness_journey_events table and groups by week.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Activity, Award, Dumbbell, Flag, MessageCircle, Shield, Sparkles, Swords, Trophy, Utensils } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";

const ICONS = {
  arena_checkin: { Icon: Shield, color: "#F5C518", tint: "rgba(245,197,24,0.06)" },
  workout_session: { Icon: Dumbbell, color: "var(--accent, #2A2825)", tint: "transparent" },
  trainer_note_received: { Icon: MessageCircle, color: "#A37B00", tint: "rgba(245,197,24,0.04)" },
  ai_trainer_session: { Icon: Sparkles, color: "#F5C518", tint: "transparent" },
  meal_logged: { Icon: Utensils, color: "var(--text-tertiary, #8A8884)", tint: "transparent" },
  challenge_completed: { Icon: Trophy, color: "#F5C518", tint: "rgba(245,197,24,0.08)" },
  duel_result: { Icon: Swords, color: "#0C0C0B", tint: "transparent" },
  badge_earned: { Icon: Award, color: "#F5C518", tint: "transparent" },
  programme_milestone: { Icon: Flag, color: "var(--accent, #2A2825)", tint: "transparent" },
  arena_pass_started: { Icon: Activity, color: "#F5C518", tint: "rgba(245,197,24,0.06)" },
  gym_linked: { Icon: Shield, color: "#A37B00", tint: "rgba(245,197,24,0.06)" },
};

function weekStart(iso) {
  const d = new Date(iso);
  d.setDate(d.getDate() - d.getDay());
  return d.toISOString().slice(0, 10);
}

export default function ArenaJourney() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [data, setData] = useState(null);
  const [monthly, setMonthly] = useState(null);

  useEffect(() => {
    apiClient.get("/arena/journey").then((r) => setData(r.data));
    apiClient.get("/arena/journey/monthly-card").then((r) => setMonthly(r.data));
  }, []);

  if (!data) return <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg-base, #ECEAE6)" }}>Loading…</div>;

  // Group events by week
  const groups = {};
  (data.events || []).forEach((e) => {
    const wk = weekStart(e.event_at);
    if (!groups[wk]) groups[wk] = [];
    groups[wk].push(e);
  });

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg-base, #ECEAE6)" }} data-testid="arena-journey">
      <header style={{ position: "sticky", top: 0, zIndex: 10, background: "var(--bg-elevated, #F0EEEA)", borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))" }}>
        <div className="max-w-md mx-auto flex items-center px-4 py-3 gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" style={{ background: "transparent", border: "none" }}><ChevronLeft size={22} /></button>
          <span style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>My Journey</span>
        </div>
      </header>
      <main className="max-w-md mx-auto px-5 pt-4">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }} data-testid="journey-stats">
          <Stat label="Workouts" value={data.stats.workouts} />
          <Stat label="Check-ins" value={data.stats.checkins} />
          <Stat label="Best Streak" value={data.stats.longest_streak || 0} />
          <Stat label="Total Reps" value={data.stats.total_reps} />
        </div>

        {monthly && monthly.workouts + monthly.checkins > 0 && (
          <MonthlyJourneyCard monthly={monthly} userName={(user?.name || "Member").split(" ")[0]} />
        )}

        <div style={{ marginTop: 24 }}>
          {Object.keys(groups).length === 0 ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <div style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>Your journey starts here</div>
              <p style={{ fontSize: 13, color: "var(--text-tertiary, #8A8884)", marginTop: 6 }}>Complete a workout or check in at a gym to see events here.</p>
            </div>
          ) : (
            Object.keys(groups).sort().reverse().map((wk) => (
              <section key={wk} style={{ marginTop: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary, #8A8884)", marginBottom: 8 }}>
                  Week of {new Date(wk).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </div>
                <div style={{ background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 14, overflow: "hidden" }}>
                  {groups[wk].map((e, i) => <JourneyEvent key={e.event_id || i} event={e} />)}
                </div>
              </section>
            ))
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 12, padding: "10px 8px", textAlign: "center" }}>
      <div style={{ fontFamily: "Barlow Condensed", fontSize: 26, fontWeight: 900, lineHeight: 1 }}>{value}</div>
      <div style={{ fontFamily: "Barlow", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-tertiary, #8A8884)", marginTop: 4 }}>{label}</div>
    </div>
  );
}

function JourneyEvent({ event }) {
  const cfg = ICONS[event.event_type] || { Icon: Activity, color: "var(--text-tertiary, #8A8884)", tint: "transparent" };
  const { Icon } = cfg;
  return (
    <div style={{ display: "flex", gap: 12, padding: 12, background: cfg.tint, borderBottom: "1px solid var(--border, rgba(12,12,11,0.06))", alignItems: "center" }}>
      <div style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(12,12,11,0.04)", display: "grid", placeItems: "center", color: cfg.color, flexShrink: 0 }}>
        <Icon size={16} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Barlow", fontSize: 14, fontWeight: 600, color: "var(--text-primary, #0C0C0B)" }}>{event.event_title}</div>
        {event.event_description && <div style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", marginTop: 2 }}>"{event.event_description.slice(0, 80)}{event.event_description.length > 80 ? "…" : ""}"</div>}
        <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", marginTop: 2 }}>
          {new Date(event.event_at).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false })}
        </div>
      </div>
    </div>
  );
}

function MonthlyJourneyCard({ monthly, userName }) {
  return (
    <div data-testid="monthly-journey-card" style={{
      marginTop: 18, padding: 24,
      background: "#0C0C0B", color: "white", borderRadius: 20,
      aspectRatio: "4/5", display: "flex", flexDirection: "column",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <img src="/assets/arena/fitfrik-shield.jpg" alt="" width={20} height={20} style={{ borderRadius: "50%" }} />
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.40)", fontWeight: 600 }}>{userName}'s month</span>
      </div>
      <div style={{ marginTop: 18 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.16em", color: "rgba(255,255,255,0.40)" }}>
          Your {monthly.month_label}
        </div>
        <div style={{ fontFamily: "Barlow Condensed", fontSize: 32, fontWeight: 900, lineHeight: 1.05, marginTop: 4 }}>IN FITNESS</div>
      </div>
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 24, alignContent: "center" }}>
        {[
          { label: "WORKOUTS", val: monthly.workouts },
          { label: "CHECK-INS", val: monthly.checkins },
          { label: "TOTAL REPS", val: monthly.total_reps },
          { label: "FITPOINTS", val: monthly.fitpoints },
        ].map((s) => (
          <div key={s.label}>
            <div style={{ fontFamily: "Barlow Condensed", fontSize: 38, fontWeight: 900, color: "#F5C518", lineHeight: 1 }}>{s.val}</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.50)", letterSpacing: "0.08em", marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      {monthly.gym_name && (
        <div style={{ fontStyle: "italic", fontSize: 12, color: "rgba(255,255,255,0.40)", marginTop: 12 }}>Trained at {monthly.gym_name} Arena</div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", color: "rgba(255,255,255,0.22)", textTransform: "uppercase" }}>
        <span>Train. Belong. Grow.</span><span>fitfrik.com</span>
      </div>
    </div>
  );
}
